import { useEffect, useMemo, useState } from "react";
import { SortableTree } from "dnd-kit-sortable-tree";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { t } from "@/i18n-js/instance";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { IconButton } from "../../uikit/HeaderV3/IconButton";
import type { BaseBlock } from "../DataModel.types";
import { SidebarHeader } from "../Sidebar/SidebarHeader";
import { SidebarHeaderTitle } from "../Sidebar/SidebarHeaderTitle";
import type { TreeItem } from "./LayersTreeItem";
import { LayersTreeItem } from "./LayersTreeItem";
import {
  areTreesEqual,
  convertBlocksToTree,
  convertTreeToBlocks,
} from "./utils";

export const Layers = () => {
  const { closeSidebarView, sortBlocks } = useBuilderStore(state => ({
    closeSidebarView: state.closeSidebarView,
    sortBlocks: state.sortBlocks,
  }));
  const root = useBuilderStore(state => state.getBlocks());
  const [items, setItems] = useState<TreeItem[]>([]);

  const rootChildren = useMemo(
    () =>
      Array.isArray(root?.props?.children?.value)
        ? (root.props.children.value as BaseBlock[])
        : [],
    [root?.props?.children?.value],
  );

  useEffect(() => {
    const collapsedMap = new Map<string, boolean>();
    const saveCollapsedState = (treeItems: TreeItem[]) => {
      treeItems.forEach(item => {
        collapsedMap.set(item.id, item.collapsed ?? true);
        if (item.children) saveCollapsedState(item.children);
      });
    };
    saveCollapsedState(items);

    const newTree = convertBlocksToTree(rootChildren);
    const restoreCollapsedState = (treeItems: TreeItem[]): TreeItem[] =>
      treeItems.map(item => ({
        ...item,
        collapsed: collapsedMap.has(item.id)
          ? collapsedMap.get(item.id)
          : item.collapsed,
        children: item.children
          ? restoreCollapsedState(item.children)
          : item.children,
      }));
    setItems(restoreCollapsedState(newTree));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keeping `items` outside of dependency array
  }, [rootChildren]);

  const handleItemsChange = (newItems: TreeItem[]) => {
    setItems(newItems);

    if (areTreesEqual(items, newItems)) {
      return;
    }
    const reorderedBlocks = convertTreeToBlocks(newItems, rootChildren);
    sortBlocks(reorderedBlocks);
  };

  return (
    <div className="divide-primary flex h-full w-[18rem] flex-col divide-y">
      <SidebarHeader>
        <div className="flex w-full items-center justify-between">
          <SidebarHeaderTitle>
            {t("builder_ui.layers.title")}
          </SidebarHeaderTitle>
          <IconButton
            name="24-close"
            iconSize={24}
            ariaLabel={t("close")}
            onClick={closeSidebarView}
          />
        </div>
      </SidebarHeader>

      <div className="flex h-full w-full flex-col overflow-hidden">
        <OverlayScrollbarsComponent>
          <ul className="py-2">
            <SortableTree
              items={items}
              onItemsChanged={handleItemsChange}
              TreeItemComponent={LayersTreeItem}
              indentationWidth={24}
              sortableProps={{ animateLayoutChanges: () => false }}
              pointerSensorOptions={{
                activationConstraint: {
                  distance: 5,
                },
              }}
            />
          </ul>
        </OverlayScrollbarsComponent>
      </div>
    </div>
  );
};
